import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { appointmentsService } from '@/services/appointments';
import { getApiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calendar as CalendarIcon, Clock, User, Stethoscope } from '@phosphor-icons/react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './DoctorCalendarPage.css';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export default function DoctorCalendarPage() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date(2025, 0, 25)); // January 25, 2025
  const [currentView, setCurrentView] = useState('week'); // Track current view

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const response = await appointmentsService.getAll();
        const appts = response.data;
        setAppointments(appts);

        // Convert appointments to calendar events
        const calendarEvents = appts.map((apt) => {
          const [hours, minutes] = apt.time.split(':');
          const date = new Date(apt.date);
          date.setHours(parseInt(hours), parseInt(minutes));
          
          const endDate = new Date(date);
          endDate.setMinutes(endDate.getMinutes() + 30); // 30 min appointments

          return {
            id: apt.id,
            title: `${apt.patientName} - ${apt.type}`,
            start: date,
            end: endDate,
            resource: apt,
          };
        });

        setEvents(calendarEvents);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      }
    };

    fetchAppointments();
  }, []);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (newView) => {
    setCurrentView(newView);
  };

  const eventStyleGetter = (event) => {
    const status = event.resource.status;
    let backgroundColor = '#0EA5A4';
    
    if (status === 'Completed') {
      backgroundColor = '#22C55E';
    } else if (status === 'Cancelled') {
      backgroundColor = '#EF4444';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  const CustomEvent = ({ event }) => {
    return (
      <div className="p-1">
        <p className="font-medium text-xs">{event.resource.patientName}</p>
        <p className="text-[10px] opacity-90">{event.resource.type}</p>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="doctor-calendar-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">My Calendar</h1>
        <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
          View and manage your appointment schedule
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Today's Appointments", value: '8', color: 'text-foreground' },
          { label: 'Completed', value: '5', color: 'text-success' },
          { label: 'Upcoming', value: '3', color: 'text-primary' },
          { label: 'Cancelled', value: '0', color: 'text-destructive' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="p-4 bg-card">
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              <p className={`text-2xl font-medium mt-1 ${stat.color}`}>{stat.value}</p>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="p-6 bg-card">
          <div style={{ height: '600px' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              date={currentDate}
              view={currentView}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              onSelectEvent={handleSelectEvent}
              style={{ height: '100%' }}
              eventPropGetter={eventStyleGetter}
              components={{
                event: CustomEvent,
              }}
              views={['month', 'week', 'day']}
            />
          </div>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card className="p-6 bg-card">
          <h3 className="text-lg font-medium mb-4">Upcoming Appointments</h3>
          <div className="space-y-3">
            {appointments.filter(a => a.status === 'Scheduled').slice(0, 5).map((apt, index) => (
              <motion.div 
                key={apt.id} 
                className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 bg-background cursor-pointer"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => {
                  const event = events.find((e) => e.id === apt.id);
                  if (event) {
                    handleSelectEvent(event);
                  } else {
                    setSelectedEvent({
                      id: apt.id,
                      title: `${apt.patientName} - ${apt.type}`,
                      start: new Date(),
                      end: new Date(),
                      resource: apt,
                    });
                    setIsDialogOpen(true);
                  }
                }}
              >
                <div className="flex-1">
                  <p className="font-medium">{apt.patientName}</p>
                  <p className="text-sm text-muted-foreground">
                    {apt.type} • {apt.date} at {apt.time}
                  </p>
                </div>
                <Badge>{apt.status}</Badge>
              </motion.div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Appointment Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
            <DialogDescription>
              Complete information about this appointment
            </DialogDescription>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <User className="h-8 w-8 text-primary" weight="duotone" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Patient</p>
                  <p className="font-medium text-lg">{selectedEvent.resource.patientName}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                  <CalendarIcon className="h-5 w-5 text-primary" weight="duotone" />
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="font-medium">{selectedEvent.resource.date}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                  <Clock className="h-5 w-5 text-primary" weight="duotone" />
                  <div>
                    <p className="text-xs text-muted-foreground">Time</p>
                    <p className="font-medium">{selectedEvent.resource.time}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-5 w-5 text-primary" weight="duotone" />
                    <span className="text-sm font-medium">Type</span>
                  </div>
                  <Badge variant="secondary">{selectedEvent.resource.type}</Badge>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm font-medium">Status</span>
                  <Badge variant={
                    selectedEvent.resource.status === 'Completed' ? 'secondary' :
                    selectedEvent.resource.status === 'Cancelled' ? 'destructive' : 'default'
                  }>
                    {selectedEvent.resource.status}
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm font-medium">Department</span>
                  <span className="text-sm">{selectedEvent.resource.department}</span>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setIsDialogOpen(false)}>
                  Close
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    const aptId = selectedEvent.resource?.id;
                    if (!aptId) return;
                    setIsDialogOpen(false);
                    navigate(`/appointments/${aptId}`, {
                      state: { openConsultation: true },
                    });
                  }}
                >
                  View Full Details
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
